/**
 * Utility functions for API URLs and configurations
 */

/**
 * Get the base API URL from environment variables
 */
export const getApiUrl = (): string => {
  // If we're on the production domain, use relative paths
  if (typeof window !== 'undefined' && window.location.hostname === 'guideme.elevate.com.es') {
    return '';
  }
  
  // Otherwise use the environment variable
  return import.meta.env.VITE_API_URL || 'http://localhost:3001';
};

/**
 * Create a full API endpoint URL
 */
export const createApiUrl = (endpoint: string): string => {
  const baseUrl = getApiUrl();
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}/${cleanEndpoint}`;
};

/**
 * Create a full URL for uploaded files (profile images, documents, etc.)
 * 
 * IMPORTANT: Fixed production image loading issue (2024-01)
 * - Production: Returns relative path `/uploads/...` (nginx proxies to backend)
 * - Development: Returns full URL `http://localhost:3021/uploads/...`
 * 
 * This ensures user profile images work correctly in both environments
 * without affecting existing uploaded files.
 */
export const createFileUrl = (filePath: string): string => {
  if (!filePath) return '';
  
  // Remove leading slash from filePath if present
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
  
  // For production domain, use relative paths (nginx will proxy to backend)
  if (typeof window !== 'undefined' && window.location.hostname === 'guideme.elevate.com.es') {
    return `/${cleanPath}`;
  }
  
  // For development, use the full API URL without /api suffix
  const apiUrl = getApiUrl();
  const baseUrl = apiUrl.replace('/api', '');
  return `${baseUrl}/${cleanPath}`;
};

/**
 * Create headers for API requests with authentication
 */
export const createAuthHeaders = (token: string): Record<string, string> => {
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Create headers for FormData requests (without Content-Type)
 */
export const createFormHeaders = (token: string): Record<string, string> => {
  return {
    'Authorization': `Bearer ${token}`,
  };
};